// Lib
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Capacitor } from '@capacitor/core';
import { useSelector } from 'react-redux';

// Utils
import {
    getDisplayMode,
    getElementId,
    getFileType,
    getFileUrl,
    getMediaAudioOnly,
} from '../../../common/elements/utils/elementPropertyUtils';
import { elementClassNames } from '../utils/elementUtil';
import { getFileData } from './utils/fileElementUtils';
import { isVideoFile } from './utils/fileGetMediaPlayerType';
import { getPlatformDetailsSelector } from '../../platform/platformSelector';
import { isApplicationPlatformMobileWeb } from '../../../common/platform/applicationPlatformUtils';

import FileIconView from './fileIconView/FileIconView';
import FileDetailView from './fileDetailView/FileDetailView';
import { PDFViewer } from '../../../capacitor_plugins/pdfViewer';

// Constants
import { ELEMENT_DISPLAY_MODES } from '../../../common/elements/elementDisplayModeConstants';
import { CAPACITOR_PDF_VIEWER_PLUGIN } from '../../../capacitor_plugins/pluginConstants';
import { FileTypes } from '../../../common/files/fileTypes';
import { withPayloadLogging } from '../../capacitor/capacitorPayloadLogging';
import { PlatformType } from '../../../common/platform/platformTypes';

const File = (props) => {
    const {
        element,
        attachment,
        dispatchNavigateToElement,
        elementEvents,
        isPresentationModeEnabled,
        isReplaceModeHovered,
        dispatchDeselectAllElements,
    } = props;

    const displayMode = getDisplayMode(element);

    const elementId = getElementId(element);
    const className = elementClassNames('File', { ...props, isReplaceModeHovered });

    const platformDetails = useSelector(getPlatformDetailsSelector);
    const applicationPlatform = platformDetails.applicationPlatform;
    const [showIphoneVideoPlayerModal, setShowIphoneVideoPlayerModal] = React.useState(false);

    const openElementViewer = useCallback(
        (event) => {
            event && event.preventDefault();
            event && event.stopPropagation();

            // If the file is in detail view and we're in presentation mode, then just rely on focus mode modals
            if (displayMode === ELEMENT_DISPLAY_MODES.DETAIL_VIEW && isPresentationModeEnabled) return;

            const isFilePdf = getFileType(element) === FileTypes.PDF;
            if (isFilePdf && isApplicationPlatformMobileWeb(applicationPlatform)) {
                window.open(getFileUrl(element), '_blank', 'noopener,noreferrer');
                return;
            }

            // If the file is a PDF and the Capacitor PDFViewer is available, use the native viewer.
            if (isFilePdf && Capacitor.isNativePlatform && Capacitor.isPluginAvailable(CAPACITOR_PDF_VIEWER_PLUGIN)) {
                const pdfUrl = getFileUrl(element);
                withPayloadLogging(
                    `${CAPACITOR_PDF_VIEWER_PLUGIN}.presentPDFViewer`,
                    (payload) => PDFViewer.presentPDFViewer(payload),
                    { pdfUrl },
                );
                (async () => {
                    var listenerHandle;
                    listenerHandle = await PDFViewer.addListener('pdfViewerClosed', () => {
                        dispatchDeselectAllElements();
                        listenerHandle.remove();
                    });
                })();

                return;
            }

            const isVideoPlayer = isVideoFile(element) && !getMediaAudioOnly(element);
            const showIPhoneVideoPlayerPreview = isVideoPlayer && platformDetails.type === PlatformType.iphone;

            if (showIPhoneVideoPlayerPreview) {
                setShowIphoneVideoPlayerModal(true);
                return;
            }

            dispatchNavigateToElement(elementId);
        },
        [dispatchNavigateToElement, dispatchDeselectAllElements, element, displayMode, applicationPlatform],
    );

    /**
     * Open the file in a modal on double click, if the file type allows.
     */
    const elementEventUpdated = {
        ...elementEvents,
        onDoubleClick: openElementViewer,
    };

    const fileData = getFileData({ element, attachment });

    // Don't render file if it has no file data (eg. is currently being uploaded by a remote user)
    if (!fileData) return null;

    switch (displayMode) {
        case ELEMENT_DISPLAY_MODES.ICON_VIEW:
            return <FileIconView {...props} elementEvents={elementEventUpdated} className={className} />;
        case ELEMENT_DISPLAY_MODES.DETAIL_VIEW:
        default:
            return (
                <FileDetailView
                    {...props}
                    elementEvents={elementEventUpdated}
                    className={className}
                    openElementViewer={openElementViewer}
                    showIphoneVideoPlayerModal={showIphoneVideoPlayerModal}
                    setShowIphoneVideoPlayerModal={setShowIphoneVideoPlayerModal}
                />
            );
    }
};

File.propTypes = {
    currentBoardId: PropTypes.string,
    elementId: PropTypes.string,
    element: PropTypes.object.isRequired,
    attachment: PropTypes.object,
    inList: PropTypes.string,
    gridSize: PropTypes.number,
    elementEvents: PropTypes.object,
    tempSize: PropTypes.object,
    isLocked: PropTypes.bool,
    isSelected: PropTypes.bool,
    isSingleSelected: PropTypes.bool,
    isEditable: PropTypes.bool,
    isPresentational: PropTypes.bool,
    isPresentationModeEnabled: PropTypes.bool,
    permissions: PropTypes.number,
    setParentHoveredChildAcceptsDrop: PropTypes.func,
    dispatchNavigateToElement: PropTypes.func,
    isReplaceModeHovered: PropTypes.bool,
    dispatchDeselectAllElements: PropTypes.func,
};

export default File;
